import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get status name
const getStatusName = (status) => {
  return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get branch name (if needed for other exports)
const getBranchName = (branchId, branchesData) => {
  if (!branchesData || !Array.isArray(branchesData)) return 'Unknown';
  const branch = branchesData.find(br => parseInt(br.id) === parseInt(branchId));
  return branch ? branch.name : 'Unknown';
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF (only 3 columns)
const drawPositionsTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 12;

  // Column widths for positions - Only 3 columns as requested
  const colWidths = [15, 70, 30]; // #, Position Name, Status

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(255, 255, 255);
  doc.setTextColor(25, 118, 210);
  doc.setFont(undefined, 'bold');

  const headers = ['No.', 'Position Name', 'Status'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(25, 118, 210);
      doc.setFont(undefined, 'bold');
      
      // Draw header background
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      // Draw header underline
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Excel export for Positions - Shows all attributes
export const exportPositionsToExcel = (positions, branchesData = [], filters = {}) => {
  try {
    // Prepare data for Excel with all attributes
    const excelData = positions.map((position, index) => ({
      'No.': index + 1,
      'Position ID': position.id,
      'Position Name': position.name,
      'Status ID': position.status,
      'Status': getStatusName(position.status),
      'Description': position.description,
      'Branch ID': position.branch_id,
      'Branch': getBranchName(position.branch_id, branchesData),
      'Created Date': new Date(position.created_at).toLocaleDateString('en-GB'),
      'Created Time': new Date(position.created_at).toLocaleTimeString('en-GB'),
      'Updated Date': new Date(position.updated_at).toLocaleDateString('en-GB'),
      'Updated Time': new Date(position.updated_at).toLocaleTimeString('en-GB'),
      'Created At Full': new Date(position.created_at).toLocaleString('en-GB'),
      'Updated At Full': new Date(position.updated_at).toLocaleString('en-GB')
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Positions');

    // Generate Excel file and download
    const fileName = `positions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Positions Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// Enhanced PDF export for Positions - Shows only 3 columns
export const exportPositionsToPDFEnhanced = (positions, filters = {}) => {
  try {
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
      title: 'Positions Report',
      subject: 'Positions Export',
      author: companyInfo.name,
      keywords: 'positions, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('POSITIONS REPORT', 105, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Positions: ${positions.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters.name || filters.dateFrom || filters.dateTo) {
      doc.text('Applied Filters:', 14, currentY);
      currentY += 6;
      
      if (filters.name) {
        doc.text(`• Position Name contains: ${filters.name}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateFrom) {
        doc.text(`• From date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateTo) {
        doc.text(`• To date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with only 3 columns
    const tableData = positions.map((position, index) => [
      (index + 1).toString(),
      position.name,
      getStatusName(position.status)
    ]);
    
    // Draw the table
    drawPositionsTable(doc, tableData, currentY + 5);
    
    const fileName = `positions_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Positions PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// CSV export for Positions - Shows all attributes
export const exportPositionsToCSV = (positions, branchesData = []) => {
  try {
    const headers = [
      'ID',
      'Position Name',
      'Status ID',
      'Status',
      'Description',
      'Branch ID',
      'Branch Name',
      'Created Date',
      'Created Time',
      'Updated Date',
      'Updated Time',
      'Created At Full',
      'Updated At Full'
    ];
    
    const csvData = positions.map(position => [
      position.id,
      `"${position.name.replace(/"/g, '""')}"`,
      position.status,
      getStatusName(position.status),
      `"${(position.description || '').replace(/"/g, '""')}"`,
      position.branch_id,
      `"${getBranchName(position.branch_id, branchesData).replace(/"/g, '""')}"`,
      `"${new Date(position.created_at).toLocaleDateString('en-GB')}"`,
      `"${new Date(position.created_at).toLocaleTimeString('en-GB')}"`,
      `"${new Date(position.updated_at).toLocaleDateString('en-GB')}"`,
      `"${new Date(position.updated_at).toLocaleTimeString('en-GB')}"`,
      `"${new Date(position.created_at).toLocaleString('en-GB')}"`,
      `"${new Date(position.updated_at).toLocaleString('en-GB')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `positions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Positions CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};